// Live Work assistant draft.
//
// Pi streams message_start / message_update / message_end. Token frames are
// live-only (not in the persisted event log), so the draft is kept here and
// exposed through useCoworkerStore.liveDraftByTask; lib/workTranscript.js
// projects it into the transcript as `cw-<task>-streaming` and, once
// message_end has landed, as the paced prefix of the official
// `cw-<task>-<seq>` row. Nothing here writes to the chat store.
//
// Work receives most of its text in a few big frames (a buffered SSE flush,
// an Arya message_end, a fat text_delta) rather than token by token, so the
// pacer reveals it a few dozen characters per tick the way chat flushes
// tokens. PACE_BUDGET_MS bounds a typical reveal, but the per-second rate is
// capped near model speed: cramming a long essay into the budget grew the row
// by whole paragraphs per tick and shoved a mobile reader down the page
// faster than they could scroll against it. PACE_HARD_MAX_MS is the ceiling
// for very long replies. The draft always reaches the full text, even if the
// run settles or the subscription is torn down mid-reveal, and then clears so
// the official row takes over unchanged.

import { Platform } from 'react-native';
import { useCoworkerStore } from '@/store/coworker';
import { applyWorkStreamDelta } from '@/constants/work';

// Same cadence as chat's socket token flusher: native gets fewer, larger
// paints per second (fewer re-renders / scroll corrections), web can take 50ms.
export const WORK_STREAM_FLUSH_MS = Platform.OS === 'web' ? 50 : 100;
export const PACE_BUDGET_MS = 3500;
export const PACE_HARD_MAX_MS = 20000;
// A fast model streams a few hundred chars/s; 900 keeps Work snappy without
// outrunning a reader.
export const PACE_MAX_CHARS_PER_SEC = 900;
// 24 chars per 50ms tick; scaled so a slower native flush keeps the same floor rate.
const MIN_CHARS_PER_FLUSH = Math.round((24 * WORK_STREAM_FLUSH_MS) / 50);
const MAX_CHARS_PER_FLUSH = Math.max(
  MIN_CHARS_PER_FLUSH,
  Math.round((PACE_MAX_CHARS_PER_SEC * WORK_STREAM_FLUSH_MS) / 1000),
);

const drafts = new Map();

function publish(taskId) {
  const draft = drafts.get(taskId);
  const store = useCoworkerStore.getState();
  if (!draft) {
    store.setLiveDraft(taskId, null);
    return;
  }
  const { state } = draft;
  store.setLiveDraft(taskId, {
    content: state.content,
    thinking: state.thinking,
    isThinking: !!state.isThinking,
    paintedContent: state.paintedContent,
    paintedThinking: state.paintedThinking,
    startedAt: draft.startedAt,
    officialSeq: draft.officialSeq,
    ending: !!draft.ending,
  });
}

function ensureDraft(taskId) {
  let draft = drafts.get(taskId);
  if (!draft) {
    draft = {
      state: { content: '', thinking: '', isThinking: false, open: true, paintedContent: '', paintedThinking: '' },
      timer: null,
      ending: false,
      officialSeq: null,
      paceStartedAt: 0,
      startedAt: Date.now(),
    };
    drafts.set(taskId, draft);
  }
  return draft;
}

function forgetDraft(taskId) {
  const draft = drafts.get(taskId);
  if (draft?.timer) clearTimeout(draft.timer);
  drafts.delete(taskId);
  publish(taskId);
}

function alignPrefix(target, painted) {
  const goal = String(target || '');
  const shown = String(painted || '');
  if (goal.startsWith(shown)) return shown;
  let i = 0;
  const n = Math.min(shown.length, goal.length);
  while (i < n && shown[i] === goal[i]) i += 1;
  return goal.slice(0, i);
}

function ticksLeft(budgetMs, elapsed) {
  return Math.max(1, Math.ceil((budgetMs - elapsed) / WORK_STREAM_FLUSH_MS));
}

/**
 * Chars to reveal this tick: enough to land inside PACE_BUDGET_MS, but never
 * faster than the rate cap unless PACE_HARD_MAX_MS would otherwise be missed.
 */
function charsThisTick(draft, behind) {
  if (behind <= 0) return 0;
  const elapsed = Math.max(0, Date.now() - (draft.paceStartedAt || Date.now()));
  const budgetPace = Math.ceil(behind / ticksLeft(PACE_BUDGET_MS, elapsed));
  const hardPace = Math.ceil(behind / ticksLeft(PACE_HARD_MAX_MS, elapsed));
  return Math.max(MIN_CHARS_PER_FLUSH, Math.min(budgetPace, MAX_CHARS_PER_FLUSH), hardPace);
}

function nextPainted(target, painted, maxChars) {
  const goal = String(target || '');
  const shown = alignPrefix(goal, painted);
  const behind = goal.length - shown.length;
  if (behind <= 0) return goal;
  const budget = Math.min(maxChars, behind);
  if (budget >= behind) return goal;
  // Break on whitespace when one is close by, so markdown does not render
  // half a word or a lone fence character for a frame.
  const window = goal.slice(shown.length, shown.length + budget);
  const nl = window.lastIndexOf('\n');
  const sp = window.lastIndexOf(' ');
  const cut = nl >= budget * 0.35 ? nl + 1 : (sp >= budget * 0.35 ? sp + 1 : budget);
  return goal.slice(0, shown.length + cut);
}

function isCaughtUp(draft) {
  const { state } = draft;
  return state.paintedContent === String(state.content || '')
    && state.paintedThinking === String(state.thinking || '');
}

function hasText(draft) {
  return !!(String(draft.state.content || '').trim() || String(draft.state.thinking || '').trim());
}

function flushDraft(taskId) {
  const draft = drafts.get(taskId);
  if (!draft) return;
  if (draft.timer) {
    clearTimeout(draft.timer);
    draft.timer = null;
  }
  const { state } = draft;
  // An empty ended turn is not a reply (the backend skips it too).
  if (draft.ending && !hasText(draft)) {
    forgetDraft(taskId);
    return;
  }

  const behind = Math.max(
    String(state.content || '').length - String(state.paintedContent || '').length,
    String(state.thinking || '').length - String(state.paintedThinking || '').length,
  );
  if (behind > 0 && !draft.paceStartedAt) draft.paceStartedAt = Date.now();
  const budget = charsThisTick(draft, behind);
  state.paintedContent = nextPainted(state.content, state.paintedContent, budget);
  state.paintedThinking = nextPainted(state.thinking, state.paintedThinking, budget);

  if (isCaughtUp(draft)) {
    draft.paceStartedAt = 0;
    if (draft.ending) {
      // The official row (from the message_end event) now shows the whole
      // text; the draft has nothing left to add.
      forgetDraft(taskId);
      return;
    }
  }

  publish(taskId);
  if (!isCaughtUp(draft)) scheduleFlush(taskId);
}

function scheduleFlush(taskId) {
  const draft = drafts.get(taskId);
  if (!draft || draft.timer) return;
  draft.timer = setTimeout(() => {
    draft.timer = null;
    flushDraft(taskId);
  }, WORK_STREAM_FLUSH_MS);
}

/**
 * Reduce a live assistant frame into the task's draft.
 * message_end without an open draft is ignored: the projection already
 * shows that reply from the event itself, nothing needs revealing.
 */
export function applyWorkAssistantEvent(taskId, ev) {
  if (!taskId || !ev) return;
  if (ev.type !== 'message_start' && ev.type !== 'message_update' && ev.type !== 'message_end') {
    return;
  }
  if (ev.message?.role && ev.message.role !== 'assistant') return;

  if (ev.type === 'message_start') {
    // A previous draft still revealing has its official row already (or
    // never got a message_end, in which case the server has no row either).
    if (drafts.has(taskId)) forgetDraft(taskId);
    const draft = ensureDraft(taskId);
    draft.state = { ...applyWorkStreamDelta(null, ev), paintedContent: '', paintedThinking: '' };
    draft.ending = false;
    draft.officialSeq = null;
    draft.paceStartedAt = 0;
    draft.startedAt = Date.now();
    publish(taskId);
    return;
  }

  let open = drafts.get(taskId);
  if (!open && ev.type === 'message_update') {
    open = ensureDraft(taskId);
    open.state = { content: '', thinking: '', isThinking: false, open: true, paintedContent: '', paintedThinking: '' };
    open.ending = false;
    open.officialSeq = null;
    open.paceStartedAt = 0;
    open.startedAt = Date.now();
  }
  if (!open) return;
  if (open.ending) return;

  const painted = { content: open.state.paintedContent, thinking: open.state.paintedThinking };
  open.state = {
    ...applyWorkStreamDelta(open.state, ev),
    paintedContent: painted.content || '',
    paintedThinking: painted.thinking || '',
  };
  // A rewritten (not appended) body invalidates what is already on screen.
  open.state.paintedContent = alignPrefix(open.state.content, open.state.paintedContent);
  open.state.paintedThinking = alignPrefix(open.state.thinking, open.state.paintedThinking);

  if (ev.type === 'message_end') {
    const seq = Number(ev.seq);
    open.ending = true;
    open.officialSeq = Number.isFinite(seq) && seq > 0 ? seq : null;
    if (!open.officialSeq || !hasText(open)) {
      // No official row will exist for this turn; nothing to reveal into.
      forgetDraft(taskId);
      return;
    }
    flushDraft(taskId);
    return;
  }

  scheduleFlush(taskId);
}

/**
 * The run is over (settled, unsubscribed, or the thread was left). A draft
 * with an official row keeps revealing to the end, bounded by
 * PACE_BUDGET_MS. A draft that never got its message_end has no row on the
 * server either; drop it so the transcript matches what a reload shows.
 */
export function clearWorkAssistantStream(taskId) {
  const draft = drafts.get(taskId);
  if (!draft) return;
  if (!draft.officialSeq) {
    forgetDraft(taskId);
    return;
  }
  draft.ending = true;
  if (isCaughtUp(draft)) {
    forgetDraft(taskId);
    return;
  }
  scheduleFlush(taskId);
}

export function hasOpenWorkAssistantDraft(taskId) {
  const draft = drafts.get(taskId);
  return !!(draft && !draft.ending);
}

export function __resetWorkAssistantStreamForTests() {
  for (const taskId of [...drafts.keys()]) forgetDraft(taskId);
}
